import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanMatchFn,
  Data,
  Route,
  Router,
} from '@angular/router';

import { map, Observable, switchMap } from 'rxjs';

import { BffUserService } from './bff.user.service';
import { PermissionService } from './permission.service';

type PermissionRouteData = Data & {
  /** A single permission name, or a list. */
  permission?: string | string[];
  /** When `true` and `permission` is a list, every permission is required. Defaults to any. */
  requireAllPermissions?: boolean;
};

function requiredPermissions(data: PermissionRouteData): string[] {
  const required = data.permission;
  if (!required) return [];
  return Array.isArray(required) ? required : [required];
}

function checkPermissions(
  data: PermissionRouteData
): boolean | Observable<boolean | ReturnType<Router['parseUrl']>> {
  const bff = inject(BffUserService);
  const permissionService = inject(PermissionService);
  const router = inject(Router);

  const permissions = requiredPermissions(data);
  if (permissions.length === 0) return true;

  const requireAll = data.requireAllPermissions === true;

  return bff.isAuthenticated().pipe(
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        return [router.parseUrl('')];
      }

      const allowed$ = requireAll
        ? permissionService.hasAll$(permissions)
        : permissionService.hasAny$(permissions);

      return allowed$.pipe(
        map((allowed) => (allowed ? true : router.parseUrl('forbidden')))
      );
    })
  );
}

/**
 * Route guard that gates on permissions from {@link PermissionService}. Usable as both
 * `canMatch` and `canActivate`.
 *
 * ```ts
 * { path: 'admin', canActivate: [permissionGuard], data: { permission: 'Users.Update' } }
 *
 * { path: 'x', canMatch: [permissionGuard],
 *   data: { permission: ['Entity.View', 'Asset.View'], requireAllPermissions: true } }
 * ```
 *
 * A route with no `permission` in its `data` is always allowed. Unauthenticated users are
 * sent to `''`; authenticated-but-missing-permission users are sent to `forbidden`.
 */
const guard = (route: Route | ActivatedRouteSnapshot) =>
  checkPermissions((route.data ?? {}) as PermissionRouteData);

export const permissionGuard = guard as unknown as CanMatchFn & CanActivateFn;
